import React, { useState, useEffect } from 'react'
import OwnerFormSection from './AdditionalOwnerFormSection'
import ValidatedDynamicFormField from '../ValidatedDynamicFormField'
import { FormFieldMapping } from '../../services/fieldMappingService'
import type { ValidationState } from '../../../validators/types'

interface PrimaryContactFormProps {
  category: string
  fields: FormFieldMapping[]
  formData: { [key: string]: any }
  errors: { [key: string]: string }
  onChange: (fieldName: string, value: any) => void
  validationState?: ValidationState
  onValidation?: (fieldName: string, isValid: boolean, message?: string) => void
}

const PrimaryContactForm: React.FC<PrimaryContactFormProps> = ({
  category,
  fields,
  formData,
  errors,
  onChange,
  validationState,
  onValidation
}) => {
  const [showOwner2, setShowOwner2] = useState(formData.hasAdditionalOwner2 || false)
  const [showOwner3, setShowOwner3] = useState(formData.hasAdditionalOwner3 || false)

  // Check if we have data for additional owners (from AI parsing)
  const hasOwner2Data = () => {
    const owner2Fields = Object.keys(formData).filter(key => key.startsWith('owner2_') && formData[key] && formData[key] !== '')
    console.log(`🔍 Checking for owner2 data: found ${owner2Fields.length} fields:`, owner2Fields)
    return owner2Fields.length > 0
  }
  
  const hasOwner3Data = () => {
    const owner3Fields = Object.keys(formData).filter(key => key.startsWith('owner3_') && formData[key] && formData[key] !== '')
    console.log(`🔍 Checking for owner3 data: found ${owner3Fields.length} fields:`, owner3Fields)
    return owner3Fields.length > 0
  }

  // Update local state when formData changes (e.g., from form reset or AI parsing)
  useEffect(() => {
    console.log('🔄 PrimaryContactForm useEffect triggered, formData keys:', Object.keys(formData))
    
    const shouldShowOwner2 = formData.hasAdditionalOwner2 || hasOwner2Data()
    const shouldShowOwner3 = formData.hasAdditionalOwner3 || hasOwner3Data()
    
    console.log(`🔍 Auto-detection results: shouldShowOwner2=${shouldShowOwner2}, showOwner2=${showOwner2}`)
    console.log(`🔍 Auto-detection results: shouldShowOwner3=${shouldShowOwner3}, showOwner3=${showOwner3}`)
    
    if (shouldShowOwner2 !== showOwner2) {
      console.log('🤖 Auto-detecting additional owner #2 from AI parsing')
      setShowOwner2(shouldShowOwner2)
      if (shouldShowOwner2) {
        onChange('hasAdditionalOwner2', true)
      }
    }
    
    if (shouldShowOwner3 !== showOwner3) {
      console.log('🤖 Auto-detecting additional owner #3 from AI parsing')
      setShowOwner3(shouldShowOwner3)
      if (shouldShowOwner3) {
        onChange('hasAdditionalOwner3', true)
      }
    }
  }, [formData, showOwner2, showOwner3])
  
  const addOwner2 = () => {
    setShowOwner2(true)
    onChange('hasAdditionalOwner2', true)
  }
  
  const addOwner3 = () => {
    setShowOwner3(true) 
    onChange('hasAdditionalOwner3', true)
  }
  
  const removeOwner2 = () => {
    setShowOwner2(false)
    onChange('hasAdditionalOwner2', false)
    
    // Clear all owner2 field data
    fields.forEach(field => {
      const fieldName = `owner2_${field.formFieldName}`
      onChange(fieldName, '')
    })
  }
  
  const removeOwner3 = () => {
    setShowOwner3(false)
    onChange('hasAdditionalOwner3', false)
    
    // Clear all owner3 field data
    fields.forEach(field => {
      const fieldName = `owner3_${field.formFieldName}`
      onChange(fieldName, '')
    })
  }

  // Helper functions for primary owner field rendering
  const mapSalesforceTypeToInputType = (salesforceType: string): string => {
    switch (salesforceType) {
      case 'string':
      case 'text': 
        return 'text'
      case 'textarea':
        return 'textarea'
      case 'email':
        return 'email'
      case 'phone':
        return 'phone'
      case 'url':
        return 'url'
      case 'select':
      case 'picklist':
        return 'select'
      case 'boolean':
        return 'boolean'
      case 'date':
      case 'datetime':
        return 'date'
      case 'number':
      case 'double':
      case 'int':
        return 'number'
      case 'currency':
        return 'currency'
      case 'percent':
        return 'percentage'
      default:
        return 'text'
    }
  }

  const getEffectiveFieldType = (field: FormFieldMapping): string => {
    return field.salesforceFieldType 
      ? mapSalesforceTypeToInputType(field.salesforceFieldType)
      : field.formFieldType
  }

  // Sort fields by display order
  const sortedFields = fields.sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder
    }
    return a.formFieldLabel.localeCompare(b.formFieldLabel)
  })

  return (
    <div className="submit-deal-form-section">
      {/* Spacer to align with sidebar header */}
      <div className="clean-form-spacer"></div>
      
      <div className="clean-form-layout">
        <div className="clean-form-sidebar">
          <h2 className="clean-form-sidebar-title">
            {category}
          </h2>
          <p className="clean-form-sidebar-description">
            Please provide primary contact information to proceed. You can add up to 2 additional owners if needed.
          </p>
          
          {/* Add Owner Buttons in Sidebar */}
          <div className="sidebar-owner-controls">
            {!showOwner2 && (
              <button 
                type="button" 
                className="btn btn-outline-primary sidebar-add-owner-btn" 
                onClick={addOwner2}
              >
                <i className="bi bi-person-plus me-2"></i>
                Add Additional Owner
              </button>
            )}
            {showOwner2 && !showOwner3 && (
              <button 
                type="button" 
                className="btn btn-outline-primary sidebar-add-owner-btn" 
                onClick={addOwner3}
              >
                <i className="bi bi-person-plus me-2"></i>
                Add Another Owner
              </button>
            )}
            {(showOwner2 || showOwner3) && (
              <div className="sidebar-owner-info">
                <i className="bi bi-info-circle me-1"></i>
                Maximum of 3 total owners allowed (1 primary + 2 additional)
              </div>
            )}
          </div>
        </div>
        
        <div className="clean-form-container">
          {/* Primary Owner Fields (directly rendered without extra header) */}
          <div className="clean-form-grid">
            {sortedFields.map((field) => (
              <div 
                key={field.formFieldName}
                className={getEffectiveFieldType(field) === 'textarea' ? 'clean-form-field-full' : 'clean-form-field-half'}
              >
                <ValidatedDynamicFormField
                  field={field}
                  value={formData[field.formFieldName] || ''}
                  onChange={onChange}
                  error={errors[field.formFieldName]}
                  validationState={validationState?.[field.formFieldName]}
                  onValidation={onValidation}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Additional Owner 2 */}
      {showOwner2 && (
        <OwnerFormSection
          title="Additional Owner #2"
          fields={fields}
          formData={formData}
          fieldPrefix="owner2_"
          errors={errors}
          onChange={onChange}
          canRemove={true}
          onRemove={removeOwner2}
        />
      )}
      
      {/* Additional Owner 3 */}
      {showOwner3 && (
        <OwnerFormSection
          title="Additional Owner #3"
          fields={fields}
          formData={formData}
          fieldPrefix="owner3_"
          errors={errors}
          onChange={onChange}
          canRemove={true}
          onRemove={removeOwner3}
        />
      )}
    </div>
  )
}

export default PrimaryContactForm
